import { useParams } from 'react-router-dom';
import queryHooks from '../../common/queryHooks/index.ts';
import DisplayTable from '../../components/DisplayTable.tsx';
import { MouseEvent, useEffect, useState } from 'react';
import { DisplayTableData } from '../../common/types/models.ts';
import moment from 'moment';
import api from '../../common/api/index.ts';
import {
  OutboundDocumentStatusEnum,
  OutboundDocumentStatusMethodEnum,
} from '../../common/enums.ts';
import ResendEpcisDocumentModal from './ResendEpcisDocumentModal.tsx';

const SentEpcisDocumentsDetail = () => {
  const { outboundDocumentId } = useParams();

  const [data, setData] = useState<DisplayTableData[]>([]);
  const [isOpenResendDocumentModal, setIsOpenResendDocumentModal] =
    useState<boolean>(false);

  const { data: outboundDocument } = queryHooks.outboundDocument.useReadOneById(
    outboundDocumentId || '',
    {
      enabled: Boolean(outboundDocumentId),
    }
  );

  useEffect(() => {
    if (!outboundDocument) return;
    const {
      outboundDocumentStatuses,
      customerLocation,
      tradingPartner,
      timestamp,
      documentIdentifier,
    } = outboundDocument;

    const last = outboundDocumentStatuses?.sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
    )[0];

    const method = last?.method;
    let statusClass = '';

    switch (last?.status?.toLocaleLowerCase()) {
      case OutboundDocumentStatusEnum.failed:
      case OutboundDocumentStatusEnum.incomplete:
        statusClass = 'red-text';
        break;
      case OutboundDocumentStatusEnum.queued:
        statusClass = 'purple-text';
        break;
      default:
        statusClass = 'green-text';
        break;
    }

    setData([
      {
        label: 'Status',
        value: (
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'space-between',
            }}
          >
            <span className={`${statusClass} bolder`}>
              {last?.status.toLocaleUpperCase() || 'SENT'} VIA{' '}
              {method === OutboundDocumentStatusMethodEnum.as2
                ? 'AS2'
                : 'EMAIL'}
            </span>
            {statusClass === 'red-text' ? (
              <a
                href="/"
                onClick={e => {
                  e.preventDefault();
                  setIsOpenResendDocumentModal(true);
                }}
              >
                Retry Send EPCIS File
              </a>
            ) : null}
          </div>
        ),
      },
      { label: 'Location', value: customerLocation.name },
      { label: 'Trading Partner', value: tradingPartner.name },
      { label: 'Date Sent', value: moment(timestamp).format('MM/DD/YYYY') },
      { label: 'Document Identifier', value: documentIdentifier },
    ]);
  }, [outboundDocument]);

  const downloadDocument = async (event: MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    if (!outboundDocumentId) return;

    const {
      outboundDocument: {
        customerLocation: { name },
        tradingPartner: { name: tpName },
        documentIdentifier,
      },
      buffer,
    } = await api.outboundDocument.download.one.by.id(outboundDocumentId);
    const uint8Array = Uint8Array.from(buffer.data);
    const blob = new Blob([uint8Array]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.hidden = true;
    link.href = url;
    link.setAttribute(
      'download',
      `${name}_to_${tpName}_${documentIdentifier.slice(-8)}.xml`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div>
      <div className="row">
        <h5 className="left">Sent Document</h5>
        <a
          href="/"
          className="right"
          onClick={downloadDocument}
          style={{ position: 'relative', top: '24px' }}
        >
          Download Epcis File
        </a>
      </div>
      <div className="row">
        <DisplayTable data={data} />
      </div>
      <div className="row" style={{ marginTop: '60px' }}>
        <h5>Sent Meds</h5>
      </div>
      <div className="row">
        <table className="highlight">
          <thead>
            <tr>
              <th>NDC</th>
              <th>Med Name</th>
              <th>Serial Number</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {outboundDocument?.outboundDocumentEpcisSgtins?.map(
              ({
                epcisSgtinId,
                epcisSgtin: { sgtin },
                epcisMedVocabulary: {
                  additionalTradeItemId,
                  regulatedProductName,
                },
              }) => (
                <tr key={epcisSgtinId}>
                  <td style={{ padding: '4px' }}>{additionalTradeItemId}</td>
                  <td style={{ padding: '4px' }}>{regulatedProductName}</td>
                  <td style={{ padding: '4px' }}>{sgtin}</td>
                </tr>
              )
            )}
          </tbody>
        </table>
      </div>
      <ResendEpcisDocumentModal
        isOpen={isOpenResendDocumentModal}
        setIsOpen={setIsOpenResendDocumentModal}
        outboundDocument={outboundDocument ?? undefined}
      />
    </div>
  );
};
export default SentEpcisDocumentsDetail;
